import React, {useEffect, useMemo, useState} from 'react'
import {
	Box,
	Button, Checkbox, Chip, CircularProgress,
	Container, FormControl, FormControlLabel,
	IconButton,
	Menu, MenuItem, Select,
	Switch,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow, TextField,
	Tooltip,
	Typography,
	useTheme,
}                                            from '@mui/material'
import moment                                                                            from 'moment'
import {Archive, ArrowBack, Check, Close, MoreVert, Unarchive}                            from "@mui/icons-material"
import {useNavigate}                                                                     from "react-router-dom"
import {doGet, doPost}                                                                   from "../../util/do-fetch"
import {DEBUG, logger}                                                                   from "../../util/log-utils"
import HoverTableRow                                                                     from "../HoverTableRow"
import LeftRightCenter                                                                   from "../LeftRightCenter"
import {PRODUCT_TYPES, getProductType}                                                   from "../../util/product-types"

const log = logger("Products", DEBUG)

function Products() {
	const navigate = useNavigate()
	const theme = useTheme()
	const [products, setProducts] = useState([])
	const [filtered, setFiltered] = useState([])
	const [showArchived, setShowArchived] = useState(false)
	const [category, setCategory] = useState('all')
	const [productType, setProductType] = useState('all')
	const [nameFilter, setNameFilter] = useState('')
	const [selectedIds, setSelectedIds] = useState([])
	const [bulkBusy, setBulkBusy] = useState(false)

	const categories = useMemo(() => {
		return products
			?.map(p => p.category)
			.filter((c, i, arr) => arr.findIndex(x => x.id === c.id) === i)
	}, [products])

	useEffect(() => {
		fetchProducts()
	}, [])

	useEffect(() => {
		if (!products) return setFiltered([])

		let list = showArchived ? products : products.filter(p => p.archived_at === null)

		if (category !== 'all') {
			list = list.filter(p => p.category.id === category)
		}

		if (productType !== 'all') {
			list = list.filter(p => p.product_type === productType)
		}

		if (nameFilter.trim() !== '') {
			const term = nameFilter.toLowerCase()
			list = list.filter(p => p.name.toLowerCase().includes(term))
		}

		setFiltered(list)

		// never keep a selection the admin can no longer see -- a bulk action should only
		// ever apply to visible rows
		const visibleIds = list.map(p => p.id)
		setSelectedIds(prev => {
			const next = prev.filter(id => visibleIds.includes(id))
			return next.length === prev.length ? prev : next
		})
	}, [products, showArchived, category, productType, nameFilter])

	const fetchProducts = async () => {
		try {
			const response = await doGet(`/admin/products?includeArchived=true`)
			const data = await response.json()
			setProducts(data)
		}
		catch (err) {
			log.error(err)
		}
	}

	const handleUpdate = async (p) => {
		setProducts(prev => [...prev].map(o => o.id === p.id ? p : o))
	}

	const handleArchive = async (p) => {
		try {
			const response = await doPost(`/admin/products/${p.id}/archive`)
			const data = await response.json()
			setProducts(prev => [...prev].map(o => o.id === data.id ? data : o))
		}
		catch (err) {
			log.error(err)
		}
	}

	const handleUnarchive = async (p) => {
		try {
			const response = await doPost(`/admin/products/${p.id}/unarchive`)
			const data = await response.json()
			setProducts(prev => [...prev].map(o => o.id === data.id ? data : o))
		}
		catch (err) {
			log.error(err)
		}
	}

	const isSelected = (id) => selectedIds.includes(id)

	const handleToggleSelect = (id) => {
		setSelectedIds(prev => prev.includes(id)
			? prev.filter(x => x !== id)
			: [...prev, id])
	}

	const handleToggleSelectAll = () => {
		setSelectedIds(prev => prev.length === filtered.length ? [] : filtered.map(p => p.id))
	}

	// Posts a bulk action, then patches the returned rows into local state so the list stays
	// a single source of truth without a full refetch.
	const doBulk = async (path, body) => {
		if (selectedIds.length === 0) return

		setBulkBusy(true)
		try {
			const options = {
				body: JSON.stringify({productIds: selectedIds, ...body})
			}
			const response = await doPost(`/admin/products/${path}`, options)
			const data = await response.json()

			if (!response.ok) {
				log.error(data)
				alert(data.errors ? data.errors.join('\n') : 'Bulk action failed.')
				return
			}

			setProducts(prev => prev.map(p => data.find(d => d.id === p.id) || p))
			setSelectedIds([])
		}
		catch (err) {
			log.error(err)
		}
		finally {
			setBulkBusy(false)
		}
	}

	const handleBulkType = (type) => doBulk('bulk-type', {productType: type})
	const handleBulkArchive = () => doBulk('bulk-archive', {})
	const handleBulkUnarchive = () => doBulk('bulk-unarchive', {})

	const allSelected = filtered.length > 0 && selectedIds.length === filtered.length
	const someSelected = selectedIds.length > 0 && !allSelected

	return (
		<Container maxWidth="lg">
			<LeftRightCenter
				left={
					<Box display={"flex"} flexDirection={"row"} alignItems={"center"}>
						<IconButton onClick={() => navigate(`/admin`)}>
							<ArrowBack/>
						</IconButton>
						<Typography variant={"h5"}>
							Products
						</Typography>
					</Box>
				}
				center={
					<Box display={"flex"}
					     flexDirection={"row"}
					     justifyContent={"center"}
					     alignItems={"center"}
					>
						<FormControl size="small" sx={{minWidth: 170, mr: 1}}>
							<Select
								value={category}
								onChange={e => setCategory(e.target.value)}
							>
								<MenuItem value="all">All Categories</MenuItem>
								{categories.map(c => (
										<MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
									))
								}
							</Select>
						</FormControl>

						<FormControl size="small" sx={{minWidth: 150, mr: 1}}>
							<Select
								value={productType}
								onChange={e => setProductType(e.target.value)}
							>
								<MenuItem value="all">All Types</MenuItem>
								{PRODUCT_TYPES.map(t => (
										<MenuItem key={t.token} value={t.token}>{t.label}</MenuItem>
									))
								}
							</Select>
						</FormControl>

						<TextField
							size="small"
							variant="outlined"
							placeholder="Filter by name"
							value={nameFilter}
							onChange={e => setNameFilter(e.target.value)}
							fullWidth
							sx={{mr: 2}}
						/>
						<FormControlLabel
							style={{marginRight: '15px'}}
							control={
								<Switch checked={showArchived}
								        onChange={() => setShowArchived(!showArchived)}
								/>
							}
							label="Archived"
						/>
					</Box>
				}
				right={
					<Box>
						<Button variant="contained" onClick={() => navigate('/admin/products/create')}>
							Create Product
						</Button>
					</Box>
				}
			/>

			{
				selectedIds.length > 0 &&
				<BulkActionBar
					count={selectedIds.length}
					busy={bulkBusy}
					onSetType={handleBulkType}
					onArchive={handleBulkArchive}
					onUnarchive={handleBulkUnarchive}
					onClear={() => setSelectedIds([])}
				/>
			}

			<TableContainer style={{border: "1px solid #ccc", borderRadius: '5px', marginTop: "10px", marginBottom: '125px'}}>
				<Table sx={{minWidth: 650, backgroundColor: theme.palette.background.paper}} aria-label="simple table">
					<TableHead>
						<TableRow>
							<TableCell padding="checkbox">
								<Checkbox
									checked={allSelected}
									indeterminate={someSelected}
									disabled={filtered.length === 0}
									onChange={handleToggleSelectAll}
									inputProps={{'aria-label': 'Select all products'}}
								/>
							</TableCell>
							<TableCell component="th">Name</TableCell>
							<TableCell component="th" align="center">Type</TableCell>
							<TableCell component="th" align="center">Questions</TableCell>
							<TableCell component="th" align="right">Created</TableCell>
							<TableCell></TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{filtered && filtered.map((row) => {
							return <ProductTableRow
										key={row.id}
										product={row}
										selected={isSelected(row.id)}
										onToggleSelect={handleToggleSelect}
										onUpdate={handleUpdate}
										onArchive={handleArchive}
										onUnarchive={handleUnarchive}

								/>
						})}
					</TableBody>
				</Table>
			</TableContainer>
		</Container>
	)
}

function BulkActionBar({count, busy, onSetType, onArchive, onUnarchive, onClear}) {
	const theme = useTheme()

	return <Box
		display="flex"
		flexDirection="row"
		alignItems="center"
		sx={{
			mt: 1,
			p: 1,
			border: `1px solid ${theme.palette.divider}`,
			borderRadius: '5px',
			backgroundColor: theme.palette.action.hover,
		}}
	>
		<Typography variant="body2" sx={{ml: 1, mr: 2, whiteSpace: 'nowrap'}}>
			<b>{count}</b> selected
		</Typography>

		<FormControl size="small" sx={{minWidth: 160, mr: 1}}>
			<Select
				value=""
				displayEmpty
				disabled={busy}
				onChange={e => onSetType(e.target.value)}
				renderValue={() => <span style={{opacity: 0.7}}>Set Type…</span>}
			>
				{PRODUCT_TYPES.map(t => (
					<MenuItem key={t.token} value={t.token}>{t.label}</MenuItem>
				))}
			</Select>
		</FormControl>

		<Button size="small"
		        startIcon={<Archive/>}
		        disabled={busy}
		        onClick={onArchive}
		        sx={{mr: 1}}
		>
			Archive
		</Button>

		<Button size="small"
		        startIcon={<Unarchive/>}
		        disabled={busy}
		        onClick={onUnarchive}
		        sx={{mr: 1}}
		>
			Unarchive
		</Button>

		{busy && <CircularProgress size={18} sx={{mr: 1}}/>}

		<Box flexGrow={1}/>

		<Button size="small"
		        startIcon={<Close/>}
		        disabled={busy}
		        onClick={onClear}
		>
			Clear
		</Button>
	</Box>
}

function ProductTypeChip({productType}) {
	const type = getProductType(productType)

	if (!type) return null

	return <Chip label={type.label} color={type.color} variant="outlined" size="small"/>
}

function ProductTableRow({product, selected, onToggleSelect, onUpdate, onArchive, onUnarchive}) {

	const navigate = useNavigate()
	const hasAllQuestions = product.total_question_count >= product.target_question_count
	const generating = !hasAllQuestions && product.generate_questions_at
	const [anchorEl, setAnchorEl] = useState(null)
	const [menuOpen, setMenuOpen] = useState(false)

	if (!product) return null

	return <HoverTableRow
		key={product.id}
		selected={selected}
		onClick={() => navigate(`/admin/products/${product.id}`)}
		sx={{
			'&:last-child td, &:last-child th': { border: 0 },
			cursor: 'pointer'
		}}
	>
		<TableCell padding="checkbox"
		           onClick={(event) => {
			           // ticking a box must not navigate into the product
			           event.stopPropagation()
		           }}
		>
			<Checkbox
				checked={selected}
				onChange={() => onToggleSelect(product.id)}
				inputProps={{'aria-label': `Select ${product.name}`}}
			/>
		</TableCell>

		<TableCell >
			<Typography style={{padding: 0, margin: 0}}>
				{product.name}
				{product.archived_at && <Chip sx={{ml: 2}} label="Archived" color="warning" variant="outlined" size="small" />}
			</Typography>
			<Box display={"flex"} flexDirection={"row"}>
				<Typography
					variant={"body2"}
					sx={{
						background: `linear-gradient(90deg, ${product.category.color}, ${product.category.color2})`,
						WebkitBackgroundClip: 'text',
						WebkitTextFillColor: 'transparent',
						mr: 1
					}}
				>
					{product.category.name}
				</Typography>
				<Typography
					variant={"body2"}
				>
					{product.token}
				</Typography>
			</Box>
		</TableCell>

		<TableCell align="center">
			<ProductTypeChip productType={product.product_type}/>
		</TableCell>

		<TableCell align="center">
			{
				hasAllQuestions
				? <Chip color="success"
				        variant="outlined"
				        label={product.total_question_count}
				        icon={<Check fontSize="small" />}
					/>
				: <Tooltip title={`${product.total_question_count} questions, ${product.target_question_count} targeted`}
				           enterDelay={1000}
				           enterNextDelay={1000}
					>
						<Chip color={generating ? 'info' : 'warning'}
						      variant={generating ? 'contained' : 'outlined'}
						      label={`${product.total_question_count} / ${product.target_question_count}`}
						      icon={generating && <CircularProgress size={14}/>}
						/>

					</Tooltip>
			}
		</TableCell>

		<TableCell align="right">
			{moment(product.created_at)
				.format('M/d/y h:mm a')}
		</TableCell>
		<TableCell align="right">
			<IconButton
				onClick={(event) => {
					event.stopPropagation()
					event.preventDefault()
					setAnchorEl(event.currentTarget)
					setMenuOpen(true)
				}}
			>
				<MoreVert/>
			</IconButton>
			<Menu
				open={menuOpen}
				anchorEl={anchorEl}
				onClose={(event) => {
					event.stopPropagation()
					setAnchorEl(null)
					setMenuOpen(false)
				}}
			>

				{
					product.archived_at
						? <MenuItem onClick={async (event) => {
							event.stopPropagation()
							setMenuOpen(false)
							await onUnarchive(product)
						  }}>
							<Unarchive sx={{mr: 2}}/> Unarchive
						  </MenuItem>
						: <MenuItem onClick={async (event) => {
							event.stopPropagation()
							setMenuOpen(false)
							await onArchive(product)
						  }}>
							<Archive sx={{mr: 2}}/> Archive
						  </MenuItem>
				}
			</Menu>
		</TableCell>
	</HoverTableRow>
}

export default Products
